import type { PagedQueryResult } from "@frui.ts/data";
import type { ListViewModel } from "@frui.ts/datascreens";
import { action, computed, decorate, observable } from "mobx";

type Constructor<TEntity> = new (...args: any[]) => ListViewModel<TEntity>;

export type SelectableListViewModel<TViewModel, TEntity extends ISelectableItem> = TViewModel & ISelectableList<TEntity>;

export interface ISelectableItem {
  id: string;
  isSelectable: boolean;
}

export interface ISelectableList<TEntity extends ISelectableItem> {
  selectedItems: Set<TEntity>;
  allItems: boolean | null;
  toggleItem(selectedItem: TEntity): void;
}

function Selectable<TEntity extends ISelectableItem, TBase extends Constructor<TEntity>>(Base: TBase) {
  return decorate(
    class SelectableList extends Base implements ISelectableList<TEntity> {
      selectedItems: Set<TEntity> = new Set();

      get itemsCount() {
        return this.selectableItems.length;
      }

      get selectableItems() {
        return this.items.filter(item => item.isSelectable);
      }

      get allItems() {
        if (this.itemsCount && this.selectedItems.size >= this.itemsCount) {
          let selectedItemCountOnPage = 0;

          // We want to count the selected items only shown on this particular page.
          this.selectableItems.forEach(item => {
            const selectedItemsIterator = this.selectedItems.values();
            let currentValue = selectedItemsIterator.next().value;

            while (currentValue?.id !== undefined) {
              if (item.id === currentValue.id) {
                selectedItemCountOnPage++;
                break;
              }
              currentValue = selectedItemsIterator.next().value;
            }
          });

          // In case options are selected on other pages,
          // we want to show partially selected icon.
          if (selectedItemCountOnPage < this.itemsCount) {
            return null;
          }

          return selectedItemCountOnPage === this.itemsCount;
        } else if (this.selectedItems.size > 0) {
          return null;
        } else {
          return false;
        }
      }

      set allItems(selectAll: boolean | null) {
        // Deselects all items found on the current page,
        // not affecting the selected items on the other pages.
        this.selectableItems.forEach(item => {
          const selectedItemsIterator = this.selectedItems.values();
          let currentValue = selectedItemsIterator.next().value;

          while (currentValue?.id !== undefined) {
            if (item.id === currentValue.id) {
              this.selectedItems.delete(currentValue);
              break;
            }
            currentValue = selectedItemsIterator.next().value;
          }
        });

        if (selectAll) {
          this.selectableItems.forEach(item => this.selectedItems.add(item));
        }
      }

      toggleItem(selectedItem: TEntity) {
        if (this.selectedItems.has(selectedItem)) {
          this.selectedItems.delete(selectedItem);
        } else {
          this.selectedItems.add(selectedItem);
        }
      }

      setDataImpl([items, paging]: PagedQueryResult<TEntity>): void {
        // We want to persist selections across all pages,
        // even if the user decides to go back to a page with previous selections.
        items.forEach(item => {
          const selectedItemsIterator = this.selectedItems.values();
          let currentValue = selectedItemsIterator.next().value;

          while (currentValue?.id !== undefined) {
            if (item.id === currentValue.id) {
              this.selectedItems.delete(currentValue);
              this.selectedItems.add(item);
              break;
            }
            currentValue = selectedItemsIterator.next().value;
          }
        });
        super.setDataImpl([items, paging]);
      }
    },
    {
      allItems: computed,
      selectableItems: computed,
      itemsCount: computed,
      selectedItems: observable,
      toggleItem: action.bound,
    }
  );
}

export default Selectable;
